import { format } from "date-fns";
import {
  type RecurrenceState,
  type WeekdayCode,
  WEEKDAY_LABELS,
  previewNextOccurrences,
  summariseRecurrence,
} from "../../lib/recurrence";

/**
 * Friendly recurrence editor. Wraps the high-level preset choices with the
 * conditional UI that each preset needs (weekday picker for WEEKLY, day-of-
 * month for MONTHLY, count vs until for caps). Renders a live preview of
 * the next ~5 occurrences so authors see what their pattern produces before
 * saving.
 *
 * The component is purely presentational — caller owns `state` and supplies
 * `onChange`. Anchor is the event's local-time start, used for the preview.
 */

const WEEKDAYS_IN_ORDER: WeekdayCode[] = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

export function RecurrencePicker({
  state,
  onChange,
  anchor,
}: {
  state: RecurrenceState;
  onChange: (next: RecurrenceState) => void;
  /** Anchor date used for the preview list (the event's local startsAt). */
  anchor: Date;
}) {
  const setFreq = (freq: RecurrenceState["freq"]) => {
    if (freq === "WEEKLY" && state.byDay.length === 0) {
      // Default BYDAY to the anchor's weekday so preview is non-empty.
      const dow = anchor.getDay();
      const order: WeekdayCode[] = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
      onChange({ ...state, freq, byDay: [order[dow]!] });
    } else if (freq === "MONTHLY" && !state.byMonthDay) {
      onChange({ ...state, freq, byMonthDay: anchor.getDate() });
    } else {
      onChange({ ...state, freq });
    }
  };

  const toggleWeekday = (code: WeekdayCode) => {
    const has = state.byDay.includes(code);
    const next = has ? state.byDay.filter((c) => c !== code) : [...state.byDay, code];
    onChange({ ...state, byDay: next.sort((a, b) => WEEKDAYS_IN_ORDER.indexOf(a) - WEEKDAYS_IN_ORDER.indexOf(b)) });
  };

  const setEnd = (kind: "never" | "count" | "until") => {
    if (kind === "never") onChange({ ...state, count: null, until: null });
    else if (kind === "count") onChange({ ...state, count: state.count ?? 10, until: null });
    else onChange({ ...state, count: null, until: state.until ?? format(anchor, "yyyy-MM-dd") });
  };

  const previews = previewNextOccurrences(state, anchor, 5);

  return (
    <div className="recur-picker">
      <div className="recur-row">
        <label className="recur-label">Repeat</label>
        <div className="recur-segments">
          {(["NONE", "DAILY", "WEEKLY", "MONTHLY", "YEARLY", "CUSTOM"] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              className={`recur-seg ${state.freq === opt ? "is-active" : ""}`}
              onClick={() => setFreq(opt)}
            >
              {opt === "NONE" ? "Once" : opt.charAt(0) + opt.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {state.freq !== "NONE" && state.freq !== "CUSTOM" && (
        <>
          <div className="recur-row">
            <label className="recur-label">Every</label>
            <div className="recur-interval">
              <input
                type="number"
                min={1}
                max={99}
                value={state.interval}
                onChange={(e) => onChange({ ...state, interval: Math.max(1, Number(e.target.value) || 1) })}
                className="auth-input"
                style={{ width: 70 }}
              />
              <span className="recur-interval-unit">
                {state.freq === "DAILY" && (state.interval === 1 ? "day" : "days")}
                {state.freq === "WEEKLY" && (state.interval === 1 ? "week" : "weeks")}
                {state.freq === "MONTHLY" && (state.interval === 1 ? "month" : "months")}
                {state.freq === "YEARLY" && (state.interval === 1 ? "year" : "years")}
              </span>
            </div>
          </div>

          {state.freq === "WEEKLY" && (
            <div className="recur-row">
              <label className="recur-label">On</label>
              <div className="recur-weekdays">
                {WEEKDAYS_IN_ORDER.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={`recur-day ${state.byDay.includes(code) ? "is-active" : ""}`}
                    onClick={() => toggleWeekday(code)}
                  >
                    {WEEKDAY_LABELS[code]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {state.freq === "MONTHLY" && (
            <div className="recur-row">
              <label className="recur-label">Day of month</label>
              <input
                type="number"
                min={1}
                max={31}
                value={state.byMonthDay ?? ""}
                onChange={(e) => onChange({ ...state, byMonthDay: e.target.value ? Number(e.target.value) : null })}
                className="auth-input"
                style={{ width: 80 }}
              />
            </div>
          )}

          <div className="recur-row">
            <label className="recur-label">Ends</label>
            <div className="recur-segments">
              {(["never", "count", "until"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`recur-seg ${endKind(state) === k ? "is-active" : ""}`}
                  onClick={() => setEnd(k)}
                >
                  {k === "never" ? "Never" : k === "count" ? "After N times" : "On date"}
                </button>
              ))}
            </div>
          </div>

          {endKind(state) === "count" && (
            <div className="recur-row">
              <label className="recur-label">Occurrences</label>
              <input
                type="number"
                min={1}
                max={500}
                value={state.count ?? 10}
                onChange={(e) => onChange({ ...state, count: Math.max(1, Number(e.target.value) || 1) })}
                className="auth-input"
                style={{ width: 100 }}
              />
            </div>
          )}

          {endKind(state) === "until" && (
            <div className="recur-row">
              <label className="recur-label">Until</label>
              <input
                type="date"
                value={state.until ?? ""}
                onChange={(e) => onChange({ ...state, until: e.target.value || null })}
                className="auth-input"
                style={{ width: 180 }}
              />
            </div>
          )}
        </>
      )}

      {state.freq === "CUSTOM" && (
        <div className="recur-row">
          <label className="recur-label">RRULE</label>
          <input
            type="text"
            placeholder="FREQ=WEEKLY;BYDAY=TU,TH;COUNT=10"
            value={state.custom}
            onChange={(e) => onChange({ ...state, custom: e.target.value })}
            className="auth-input"
            style={{ flex: 1 }}
          />
        </div>
      )}

      <div className="recur-summary">
        <p className="recur-summary-line">{summariseRecurrence(state)}</p>
        {previews.length > 1 && state.freq !== "CUSTOM" && (
          <ol className="recur-preview">
            {previews.slice(0, 5).map((d, i) => (
              <li key={i}>{format(d, "EEE d LLL yyyy · HH:mm")}</li>
            ))}
            {previews.length > 5 && <li className="recur-preview-more">…and more</li>}
          </ol>
        )}
      </div>
    </div>
  );
}

function endKind(state: RecurrenceState): "never" | "count" | "until" {
  if (state.count) return "count";
  if (state.until) return "until";
  return "never";
}
